import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import '@/styles/hydlibrary-theme.css';

/**
 * Shared header + footer for all Hyd Library public-facing designed pages.
 * Wraps content in a `.hydlib-scope` div so the design-system CSS variables and
 * `Libre Caslon` fonts apply without leaking to Admin (which stays untouched).
 */
function SundayBanner() {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sunday-banner`)
            .then(r => r.json())
            .then(setInfo)
            .catch(() => setInfo(null));
    }, []);
    if (!info) return null;
    const day = info.is_today ? 'Today' : 'This Sunday';
    return (
        <div style={{
            background: info.is_closed ? 'var(--a4)' : 'var(--a2)',
            color: info.is_closed ? 'var(--o4)' : 'var(--o2)',
            fontFamily: "'Libre Caslon Text', serif",
            fontStyle: 'italic',
            fontSize: 15,
            letterSpacing: 0.5,
            textAlign: 'center',
            padding: '10px 16px',
        }} data-testid="hydlib-banner">
            {info.is_closed ? (
                <>{day}, {info.date_label}, we&apos;re closed{info.closure_note ? ` — ${info.closure_note}` : ''}. Online reservations stay open as always.</>
            ) : (
                <>
                    {day}, {info.date_label}, the doors open — come browse, read, linger. 10.30am–5pm
                    {info.events.length > 0 && (
                        <> · <Link to="/events" style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3 }} data-testid="banner-events-link">On that day: {info.events.slice(0, 3).join(' & ')}{info.events.length > 3 ? ` +${info.events.length - 3} more` : ''} →</Link></>
                    )}
                </>
            )}
        </div>
    );
}

export default function PublicLayout({ children, theme = 'archive', banner = null, grid = null, gridStrength = 28 }) {
    return (
        <div className="hydlib-scope" data-theme={theme} style={{ position: 'relative' }}>
            {grid ? (
                <div className="hydlib-page-grid" aria-hidden="true" style={{
                    backgroundImage: `repeating-linear-gradient(0deg, transparent 0 44px, color-mix(in oklch, var(${grid}) ${gridStrength}%, transparent) 44px 46px), repeating-linear-gradient(90deg, transparent 0 44px, color-mix(in oklch, var(${grid}) ${gridStrength}%, transparent) 44px 46px)`,
                }} />
            ) : null}
            {banner === 'sunday' ? (
                <SundayBanner />
            ) : banner ? (
                <div style={{
                    background: 'var(--a2)',
                    color: 'var(--o2)',
                    fontFamily: "'Libre Caslon Text', serif",
                    fontStyle: 'italic',
                    fontSize: 15,
                    letterSpacing: 0.5,
                    textAlign: 'center',
                    padding: '10px 16px',
                }} data-testid="hydlib-banner">{banner}</div>
            ) : null}
            <PublicHeader />
            <main style={{ position: 'relative' }}>{children}</main>
            <PublicFooter />
        </div>
    );
}

function PublicHeader() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const [searchQ, setSearchQ] = useState('');
    const handleSearch = (e) => {
        e.preventDefault();
        const q = searchQ.trim();
        if (!q) return;
        navigate(`/catalog?search=${encodeURIComponent(q)}`);
        setSearchQ('');
    };

    const navItems = [
        { path: '/', label: 'Home' },
        { path: '/catalog', label: 'Catalog' },
        { path: '/events', label: 'Events' },
        { path: '/faqs', label: 'FAQs' },
        { path: '/contact', label: 'Contact Us' },
    ];
    if (user) navItems.push({ path: '/my-library', label: 'My Library' });

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    const handleAuthCta = () => {
        if (user) {
            if (user.role === 'staff') navigate('/admin');
            else navigate('/my-library');
        } else {
            navigate('/login');
        }
    };

    return (
        <header style={{
            position: 'relative',
            borderBottom: '1px solid var(--line)',
            background: 'var(--bg)',
            zIndex: 10,
        }}>
            <div className="hydlib-pad hydlib-header-row" style={{
                maxWidth: 1240,
                margin: '0 auto',
                padding: '18px 48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 24,
            }}>
                <Link to="/" style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 15, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--ink)', whiteSpace: 'nowrap' }} data-testid="hydlib-logo-link">
                    The Hyderabad Library
                </Link>

                {/* Desktop nav */}
                <nav className="hydlib-hide-mobile" style={{
                    display: 'flex',
                    gap: 22,
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 12,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                }}>
                    {navItems.map(item => (
                        <Link
                            key={item.path}
                            to={item.path}
                            data-testid={`hydlib-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            style={{
                                paddingBottom: 4,
                                borderBottom: isActive(item.path) ? '2px solid var(--a1)' : '2px solid transparent',
                                color: isActive(item.path) ? 'var(--a1)' : 'var(--ink)',
                                fontWeight: isActive(item.path) ? 700 : 400,
                            }}
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>

                {/* Mobile burger menu */}
                <details className="hydlib-burger" style={{ position: 'relative' }} data-testid="hydlib-burger">
                    <summary style={{ listStyle: 'none', cursor: 'pointer', fontFamily: "'Space Mono', monospace", fontSize: 18, padding: '4px 12px', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)' }}>☰</summary>
                    <div style={{ position: 'absolute', right: 0, top: '120%', zIndex: 60, background: 'var(--paper)', border: '1px solid var(--line)', boxShadow: '5px 6px 0 color-mix(in oklch, var(--line) 28%, transparent)', display: 'flex', flexDirection: 'column', minWidth: 210, fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                        <form onSubmit={handleSearch} role="search" style={{ display: 'flex', borderBottom: '1px dashed var(--line)', padding: '10px 14px', gap: 8 }}>
                            <input
                                value={searchQ}
                                onChange={(e) => setSearchQ(e.target.value)}
                                placeholder="Search books…"
                                aria-label="Search the catalog"
                                data-testid="burger-search-input"
                                style={{ flex: 1, minWidth: 0, border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontSize: 12, padding: '8px 10px' }}
                            />
                            <button type="submit" data-testid="burger-search-submit" style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--a2)', color: 'var(--o2)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, textTransform: 'uppercase', padding: '8px 12px' }}>Go</button>
                        </form>
                        {navItems.map((item, i) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                data-testid={`hydlib-burger-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                style={{ color: 'var(--ink)', padding: '13px 18px', borderBottom: (user || i < navItems.length - 1) ? '1px dashed var(--line)' : 'none' }}
                            >
                                {item.label}
                            </Link>
                        ))}
                        {user && (
                            <button
                                onClick={handleLogout}
                                data-testid="hydlib-burger-logout"
                                style={{ cursor: 'pointer', border: 'none', background: 'none', textAlign: 'left', color: 'var(--a4)', padding: '13px 18px', fontFamily: 'inherit', fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', fontWeight: 700 }}
                            >
                                Log out
                            </button>
                        )}
                    </div>
                </details>

                <form onSubmit={handleSearch} className="hydlib-hide-mobile" role="search" style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--line)', background: 'var(--paper)', padding: '0 0 0 12px', flex: '0 1 240px', minWidth: 140 }}>
                    <span style={{ opacity: 0.55, fontSize: 14, marginRight: 6 }}>⌕</span>
                    <input
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        placeholder="Search books…"
                        aria-label="Search the catalog"
                        data-testid="header-search-input"
                        style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 0.5, padding: '9px 6px 9px 0' }}
                    />
                    <button type="submit" aria-label="Search" data-testid="header-search-submit" style={{ cursor: 'pointer', border: 'none', borderLeft: '1px solid var(--line)', background: 'var(--a2)', color: 'var(--o2)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '9px 12px' }}>Go</button>
                </form>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button
                        onClick={handleAuthCta}
                        data-testid="hydlib-auth-cta"
                        className="hydlib-highlight"
                        style={{
                            cursor: 'pointer',
                            border: 'none',
                            color: 'var(--ink)',
                            fontFamily: "'Space Mono', monospace",
                            fontWeight: 700,
                            fontSize: 13,
                            letterSpacing: 1.5,
                            textTransform: 'uppercase',
                            textAlign: 'left',
                            lineHeight: 1.5,
                            padding: '9px 20px',
                            background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)`,
                        }}
                    >
                        {user
                            ? (user.role === 'staff' ? 'Admin →' : 'My Library →')
                            : (<>
                                <span className="hydlib-hide-mobile">Already a member?<br />Log in here</span>
                                <span className="hydlib-show-mobile">Log in</span>
                            </>)}
                    </button>
                    {user && (
                        <button
                            onClick={handleLogout}
                            data-testid="hydlib-logout-btn"
                            className="hydlib-hide-mobile"
                            style={{
                                cursor: 'pointer',
                                border: '1px solid var(--line)',
                                background: 'var(--paper)',
                                color: 'var(--ink)',
                                fontFamily: "'Space Mono', monospace",
                                fontWeight: 700,
                                fontSize: 11,
                                letterSpacing: 1.5,
                                textTransform: 'uppercase',
                                padding: '9px 14px',
                                opacity: 0.85,
                            }}
                        >
                            Log out
                        </button>
                    )}
                </div>
            </div>
        </header>
    );
}

function PublicFooter() {
    return (
        <footer style={{
            position: 'relative',
            background: 'var(--paper)',
            borderTop: '1px solid var(--line)',
            marginTop: 0,
        }}>
            <div className="hydlib-pad hydlib-cols" style={{
                maxWidth: 1240,
                margin: '0 auto',
                padding: '48px 48px 20px',
                display: 'grid',
                gridTemplateColumns: '1.4fr 1fr 1fr 1fr',
                gap: 40,
            }}>
                <div>
                    <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 26, marginBottom: 10 }}>The Hyderabad Library</div>
                    <p style={{ fontSize: 14, lineHeight: 1.6, opacity: 0.85, margin: 0 }}>Unlimited books. Five at a time. Zero fuss. A subscription library in Banjara Hills for the incurably curious.</p>
                </div>
                <div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 10 }}>Visit</div>
                    <div style={{ fontSize: 14, lineHeight: 1.7 }}>
                        2nd Floor, BBR Forum<br />
                        Road No. 2 Banjara Hills, Hyderabad<br />
                        <span style={{ opacity: 0.8 }}>Landmark: same building as KSR Eyecare</span><br />
                        <a href="https://maps.app.goo.gl/2EucnVthv76SzcCw6" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--a3)' }} data-testid="footer-map-link">Open in Google Maps →</a><br />
                        Sun 10.30am–5pm
                    </div>
                </div>
                <div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 10 }}>Get in touch</div>
                    <div style={{ fontSize: 14, lineHeight: 1.7 }}>
                        <a href="mailto:thehyderabadlibrary@gmail.com" style={{ color: 'var(--a3)' }}>thehyderabadlibrary@gmail.com</a><br />
                        <a href="tel:+917901662333" style={{ color: 'var(--a3)' }}>7901662333</a>
                    </div>
                </div>
                <div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 10 }}>The fine print</div>
                    <div style={{ fontSize: 14, lineHeight: 1.9 }}>
                        <Link to="/policies/terms" style={{ color: 'var(--a3)' }}>Terms</Link><br />
                        <Link to="/policies/privacy" style={{ color: 'var(--a3)' }}>Privacy</Link><br />
                        <Link to="/policies/refund" style={{ color: 'var(--a3)' }}>Refunds</Link>
                    </div>
                </div>
            </div>
            <div style={{
                borderTop: '1px solid var(--line)',
                padding: '14px 48px',
                fontFamily: "'Space Mono', monospace",
                fontSize: 11,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                textAlign: 'center',
                opacity: 0.75,
            }}>
                © {new Date().getFullYear()} The Hyderabad Library — Est. 2026
            </div>
        </footer>
    );
}
